/**
 * Trading Service for order management and market data
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface OrderRequest {
  market: string;
  side: 'buy' | 'sell';
  order_type: 'market' | 'limit' | 'stop-limit';
  amount: number;
  price?: number;
  stop_price?: number;
  time_in_force?: 'GTC' | 'IOC' | 'FOK';
}

export interface Order {
  id: number;
  user_id: number;
  market: string;
  side: 'buy' | 'sell';
  order_type: string;
  amount: number;
  price?: number;
  stop_price?: number;
  executed_amount?: number;
  executed_price?: number;
  status: 'pending' | 'completed' | 'cancelled' | 'failed' | 'partially_filled';
  external_order_id?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface MarketData {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  volume24h: number;
  minOrderSize: number;
  maxOrderSize: number;
  priceStep: number;
  quantityStep: number;
}

export interface Balance {
  symbol: string;
  available: number;
  inOrder: number;
  total: number;
}

class TradingService {
  private getAuthHeader(): string {
    let token: string | null = null;
    try {
      const stored = localStorage.getItem('authTokens');
      if (stored) {
        token = JSON.parse(stored).access_token;
      }
    } catch {
      token = null;
    }
    if (!token) {
      throw new Error('No authentication token found');
    }
    return `Bearer ${token}`;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.getAuthHeader(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get available trading markets from BTRADER
   */
  async getMarkets(): Promise<MarketData[]> {
    const response = await this.makeRequest<{ markets: any[] }>('/btrader/markets');

    return response.markets.map(market => ({
      symbol: market.market,
      name: `${market.base}/${market.quote}`,
      price: 0, // Will be updated with ticker data
      change24h: 0,
      volume24h: 0,
      minOrderSize: parseFloat(market.min_order_in_base_asset || '0'),
      maxOrderSize: 1000000, // Default high value
      priceStep: Math.pow(10, -market.price_precision),
      quantityStep: Math.pow(10, -market.base_precision),
    }));
  }

  /**
   * Get ticker data for market
   */
  async getMarketTicker(market: string): Promise<any> {
    return this.makeRequest(`/btrader/ticker/${encodeURIComponent(market)}`);
  }

  /**
   * Get account balances
   */
  async getBalances(): Promise<Balance[]> {
    const response = await this.makeRequest<{ balances: any[] }>('/btrader/balance');

    return response.balances.map(balance => ({
      symbol: balance.symbol,
      available: parseFloat(balance.available),
      inOrder: parseFloat(balance.inOrder),
      total: parseFloat(balance.available) + parseFloat(balance.inOrder),
    }));
  }

  /**
   * Place a new trading order
   */
  async createOrder(orderData: OrderRequest): Promise<Order> {
    return this.makeRequest<Order>('/trading/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  /**
   * Get user's trading orders
   */
  async getOrders(limit: number = 50): Promise<Order[]> {
    return this.makeRequest<Order[]>(`/trading/orders?limit=${limit}`);
  }

  /**
   * Get specific order by ID
   */
  async getOrder(orderId: number): Promise<Order> {
    return this.makeRequest<Order>(`/trading/orders/${orderId}`);
  }

  /**
   * Cancel an existing order
   */
  async cancelOrder(orderId: number): Promise<{ success: boolean; message: string }> {
    return this.makeRequest(`/trading/orders/${orderId}/cancel`, {
      method: 'DELETE',
    });
  }

  /**
   * Get portfolio information
   */
  async getPortfolio(): Promise<any> {
    return this.makeRequest('/portfolio/');
  }

  /**
   * Sync order status with exchange
   */
  async syncOrderStatus(): Promise<{ synced_count: number }> {
    return this.makeRequest('/trading/sync-orders', {
      method: 'POST',
    });
  }

  /**
   * Format currency amount for display
   */
  formatAmount(amount: number, currency: string, precision: number = 8): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: precision,
    }).format(amount);
  }

  /**
   * Format price for display
   */
  formatPrice(price: number, precision: number = 2): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: precision,
      maximumFractionDigits: 8,
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  }

  /**
   * Calculate order total including fees
   */
  calculateOrderTotal(amount: number, price: number, side: 'buy' | 'sell', feeRate: number = 0.0025): {
    total: number;
    fee: number;
    netAmount: number;
  } {
    const baseTotal = amount * price;
    const fee = baseTotal * feeRate;

    if (side === 'buy') {
      return {
        total: baseTotal + fee,
        fee,
        netAmount: amount,
      };
    } else {
      return {
        total: baseTotal - fee,
        fee,
        netAmount: amount,
      };
    }
  }

  /**
   * Validate order parameters
   */
  validateOrder(orderData: OrderRequest, balance: Balance, marketData: MarketData): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Amount validation
    if (orderData.amount <= 0) {
      errors.push('Menge muss größer als 0 sein');
    }

    if (orderData.amount < marketData.minOrderSize) {
      errors.push(`Mindestmenge: ${marketData.minOrderSize}`);
    }

    if (orderData.amount > marketData.maxOrderSize) {
      errors.push(`Maximalmenge: ${marketData.maxOrderSize}`);
    }

    // Price validation for limit orders
    if (orderData.order_type !== 'market' && (!orderData.price || orderData.price <= 0)) {
      errors.push('Preis ist für Limit-Orders erforderlich');
    }

    // Balance validation
    if (orderData.side === 'buy') {
      const requiredAmount = orderData.amount * (orderData.price || marketData.price);
      if (requiredAmount > balance.available) {
        errors.push('Unzureichendes Guthaben');
      }
    } else {
      if (orderData.amount > balance.available) {
        errors.push('Unzureichendes Guthaben');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get order status color
   */
  getOrderStatusColor(status: Order['status']): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
      case 'partially_filled':
        return 'warning';
      case 'cancelled':
        return 'default';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  }

  /**
   * Get order status label
   */
  getOrderStatusLabel(status: Order['status']): string {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'completed':
        return 'Ausgeführt';
      case 'cancelled':
        return 'Storniert';
      case 'failed':
        return 'Fehlgeschlagen';
      case 'partially_filled':
        return 'Teilweise ausgeführt';
      default:
        return status;
    }
  }

  /**
   * Format market symbol for display
   */
  formatMarketSymbol(market: string): { base: string; quote: string; formatted: string } {
    const [base, quote] = market.split('-');
    return {
      base,
      quote,
      formatted: `${base}/${quote}`,
    };
  }

  /**
   * Get popular trading pairs
   */
  getPopularMarkets(): string[] {
    return [
      'BTC-EUR',
      'ETH-EUR',
      'ADA-EUR',
      'DOT-EUR',
      'LINK-EUR',
      'LTC-EUR',
      'XRP-EUR',
      'MATIC-EUR',
    ];
  }

  /**
   * Calculate percentage change
   */
  calculatePercentageChange(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Format error message for user display
   */
  formatError(error: unknown): string {
    if (error instanceof Error) {
      if (error.message.includes('401')) {
        return 'Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.';
      }
      if (error.message.includes('insufficient')) {
        return 'Unzureichendes Guthaben für diese Order.';
      }
      if (error.message.includes('market')) {
        return 'Ungültiges Trading-Paar oder Markt nicht verfügbar.';
      }
      return error.message;
    }
    return 'Ein unbekannter Fehler ist aufgetreten.';
  }
}

export const tradingService = new TradingService();
export default tradingService;